import React from "react";
import { AlertCircle, CheckCircle2, Download, Eye, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  ContractAdjustmentView,
  ContractCeilingView,
  canCancelContractAdjustment,
  canDecideContractAdjustment,
  canReviewContractAdjustment,
  formatAdjustmentNumber,
  formatCents,
  formatDate,
  formatDateTime,
  formatMoneyValue,
  getAdjustmentStatusLabel,
  getAdjustmentTypeLabel,
  getDecisionErrorMessage,
  getDirectionLabel,
  getEvidenceQualificationLabel,
  getEvidenceTypeLabel,
  getProjectedContractCeilingCents,
  isClosedContractAdjustmentStatus,
  isQualifyingEvidenceType,
  parseMoneyToCents,
} from "./contractAdjustmentsUtils";

type EvidenceItem = {
  id: number;
  documentId: number;
  documentName?: string | null;
  evidenceType: string;
  documentType?: string | null;
  fileUrl?: string | null;
  createdAt?: string | Date | null;
};

type DecisionAction = "approve" | "reject" | "cancel";

type ContractAdjustmentDecisionDialogProps = {
  open: boolean;
  adjustment: ContractAdjustmentView | null;
  installationId: number;
  installationPrice: unknown;
  currentCeilingCents: number;
  ceiling: ContractCeilingView;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void | Promise<void>;
};

function DecisionDetail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-md bg-muted/40 p-3">
      <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 min-w-0 break-words [overflow-wrap:anywhere]">{value}</div>
    </div>
  );
}

function DecisionSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h4 className="text-sm font-semibold">{title}</h4>
      {children}
    </section>
  );
}

function statusBadgeClass(status: string) {
  if (status === "pending_approval") return "border-amber-200 bg-amber-50 text-amber-700";
  if (status === "approved") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "rejected") return "border-red-200 bg-red-50 text-red-700";
  return "border-gray-200 bg-gray-50 text-gray-700";
}

export function ContractAdjustmentDecisionDialog({
  open,
  adjustment,
  installationId,
  installationPrice,
  currentCeilingCents,
  ceiling,
  onOpenChange,
  onSuccess,
}: ContractAdjustmentDecisionDialogProps) {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [pendingAction, setPendingAction] = React.useState<DecisionAction | null>(null);
  const [reason, setReason] = React.useState("");
  const [localError, setLocalError] = React.useState<string | null>(null);
  const inFlightRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      setPendingAction(null);
      setReason("");
      setLocalError(null);
      inFlightRef.current = false;
    }
  }, [open]);

  const evidenceQuery = trpc.contractAdjustments.listEvidence.useQuery(
    { adjustmentId: adjustment?.id ?? 0 },
    { enabled: open && Boolean(adjustment?.id), staleTime: 30 * 1000 },
  );

  const approveMutation = trpc.contractAdjustments.approve.useMutation();
  const rejectMutation = trpc.contractAdjustments.reject.useMutation();
  const cancelMutation = trpc.contractAdjustments.cancel.useMutation();

  if (!adjustment || !canReviewContractAdjustment(user?.role)) return null;

  const canDecide = canDecideContractAdjustment(user?.role);
  const isPending = adjustment.status === "pending_approval";
  const isClosed = isClosedContractAdjustmentStatus(adjustment.status);
  const amountCents = parseMoneyToCents(adjustment.amount) ?? 0;
  const projectedCeilingCents = getProjectedContractCeilingCents(currentCeilingCents, adjustment);
  const activeMilestonesCents = parseMoneyToCents(ceiling?.activeMilestonesTotal) ?? ceiling?.committedMilestonesCents ?? null;
  const evidence = (evidenceQuery.data || []) as EvidenceItem[];
  const hasQualifyingEvidence = evidence.some(item => isQualifyingEvidenceType(item.evidenceType));
  const decisionPending = approveMutation.isPending || rejectMutation.isPending || cancelMutation.isPending;
  const approveBlockedByEvidence = evidenceQuery.isLoading || evidenceQuery.isError || !hasQualifyingEvidence;
  const rejectReasonRequired = pendingAction === "reject" && !reason.trim();
  const cancelReasonRequired = pendingAction === "cancel" && !reason.trim();

  const refetchAfterDecision = async () => {
    await Promise.allSettled([
      utils.contractAdjustments.listByInstallation.invalidate({ installationId }),
      utils.contractAdjustments.getById.invalidate({ id: adjustment.id }),
      utils.contractAdjustments.listEvidence.invalidate({ adjustmentId: adjustment.id }),
      utils.contractAdjustments.getEffectiveContractCeiling.invalidate({ installationId }),
      utils.installations.getById.invalidate({ id: installationId }),
    ]);
    await onSuccess();
  };

  const handleDecisionError = async (error: unknown) => {
    const err = error as any;
    const code = err?.data?.code || err?.code;
    const message = getDecisionErrorMessage(error);
    setLocalError(message);
    toast.error(message);
    if (code === "CONFLICT") {
      await refetchAfterDecision();
      onOpenChange(false);
    }
  };

  const runDecision = async (action: DecisionAction) => {
    if (inFlightRef.current || !adjustment) return;
    if (action === "approve" && approveBlockedByEvidence) {
      setLocalError("No se puede aprobar hasta cargar evidencias cualificantes sin errores.");
      return;
    }
    if (action === "reject" && !reason.trim()) {
      setLocalError("El motivo de rechazo es obligatorio.");
      return;
    }
    if (action === "cancel" && !reason.trim()) {
      setLocalError("El motivo de cancelación es obligatorio.");
      return;
    }

    inFlightRef.current = true;
    setLocalError(null);
    try {
      if (action === "approve") await approveMutation.mutateAsync({ id: adjustment.id });
      if (action === "reject") await rejectMutation.mutateAsync({ id: adjustment.id, rejectionReason: reason.trim() });
      if (action === "cancel") await cancelMutation.mutateAsync({ id: adjustment.id, cancellationReason: reason.trim() });
      await refetchAfterDecision();
      toast.success(action === "approve" ? "Modificación aprobada" : action === "reject" ? "Modificación rechazada" : "Modificación cancelada");
      onOpenChange(false);
    } catch (error) {
      await handleDecisionError(error);
    } finally {
      inFlightRef.current = false;
    }
  };

  const approvalCopy = `Aprobar esta modificación actualizará el importe contractual vigente de ${formatCents(currentCeilingCents)} a ${formatCents(projectedCeilingCents)}.`;
  const isDecrease = adjustment.direction === "decrease";
  const nearMilestoneLimit = isDecrease && activeMilestonesCents !== null && projectedCeilingCents - activeMilestonesCents <= Math.max(Math.round(projectedCeilingCents * 0.05), 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="break-words [overflow-wrap:anywhere]">
                {formatAdjustmentNumber(adjustment.adjustmentNumber)} - {adjustment.title}
              </DialogTitle>
              <DialogDescription>
                Enviado por usuario #{adjustment.submittedByUserId ?? "-"} el {formatDateTime(adjustment.submittedAt)}
              </DialogDescription>
            </div>
            <Badge variant="outline" className={statusBadgeClass(adjustment.status)}>
              {getAdjustmentStatusLabel(adjustment.status)}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {localError && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{localError}</span>
            </div>
          )}

          <DecisionSection title="Impacto contractual">
            <div className="grid gap-3 md:grid-cols-3">
              <DecisionDetail label="Contrato inicial" value={formatMoneyValue(installationPrice)} />
              <DecisionDetail label="Importe vigente" value={formatCents(currentCeilingCents)} />
              <DecisionDetail label="Importe firmado" value={formatCents(adjustment.direction === "decrease" ? -amountCents : amountCents, { signed: true })} />
            </div>
            <div className="rounded-md border bg-muted/20 p-4">
              <div className="flex flex-wrap items-baseline gap-2 text-lg font-semibold">
                <span>{formatCents(currentCeilingCents)}</span>
                <span>{formatCents(adjustment.direction === "decrease" ? -amountCents : amountCents, { signed: true })}</span>
                <span>=</span>
                <span>{formatCents(projectedCeilingCents)}</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">El importe contractual vigente solo cambiará si se aprueba esta modificación.</p>
              {isDecrease && activeMilestonesCents !== null && (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  Hitos activos actuales: {formatCents(activeMilestonesCents)}. El backend rechazará la aprobación si la reducción deja el techo por debajo de ese total.
                  {nearMilestoneLimit && <span className="font-medium"> La reducción queda cerca del límite disponible.</span>}
                </div>
              )}
            </div>
          </DecisionSection>

          <DecisionSection title="Datos">
            <div className="grid gap-3 md:grid-cols-2">
              <DecisionDetail label="Título" value={adjustment.title} />
              <DecisionDetail label="Referencia" value={adjustment.reference || "-"} />
              <DecisionDetail label="Motivo" value={adjustment.reason || "-"} />
              <DecisionDetail label="Tipo" value={getAdjustmentTypeLabel(adjustment.adjustmentType)} />
              <DecisionDetail label="Dirección" value={getDirectionLabel(adjustment.direction)} />
              <DecisionDetail label="Fecha efectiva" value={formatDate(adjustment.effectiveDate)} />
              <DecisionDetail label="Solicitante" value={`Usuario #${adjustment.createdByUserId ?? "-"}`} />
            </div>
          </DecisionSection>

          <DecisionSection title={`Evidencias (${evidence.length})`}>
            {evidenceQuery.isLoading && (
              <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando evidencias...
              </div>
            )}
            {evidenceQuery.isError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                No se pudieron cargar las evidencias. No se permite aprobar hasta resolver esta lectura.
              </div>
            )}
            {!evidenceQuery.isLoading && !evidenceQuery.isError && evidence.length === 0 && (
              <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">Sin evidencias vinculadas.</div>
            )}
            <div className="space-y-2">
              {evidence.map(item => (
                <div key={item.id} className="flex flex-col gap-3 rounded-md border p-3 text-sm md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0 space-y-1">
                    <div className="font-medium break-words [overflow-wrap:anywhere]">{item.documentName || "Documento sin nombre"}</div>
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      <Badge variant="outline">{getEvidenceTypeLabel(item.evidenceType)}</Badge>
                      <Badge variant={isQualifyingEvidenceType(item.evidenceType) ? "default" : "outline"}>
                        {getEvidenceQualificationLabel(item.evidenceType)}
                      </Badge>
                    </div>
                  </div>
                  {item.fileUrl && (
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <Button size="sm" variant="outline" asChild>
                        <a href={item.fileUrl} target="_blank" rel="noopener noreferrer"><Eye className="mr-2 h-4 w-4" />Ver</a>
                      </Button>
                      <Button size="sm" variant="outline" asChild>
                        <a href={item.fileUrl} download><Download className="mr-2 h-4 w-4" />Descargar</a>
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DecisionSection>

          <DecisionSection title="Historial">
            <div className="grid gap-3 md:grid-cols-2">
              <DecisionDetail label="Creación" value={`Usuario #${adjustment.createdByUserId ?? "-"} · ${formatDateTime(adjustment.createdAt)}`} />
              <DecisionDetail label="Envío a aprobación" value={`Usuario #${adjustment.submittedByUserId ?? "-"} · ${formatDateTime(adjustment.submittedAt)}`} />
              {adjustment.approvedAt && <DecisionDetail label="Aprobación" value={`Usuario #${adjustment.approvedByUserId ?? "-"} · ${formatDateTime(adjustment.approvedAt)}`} />}
              {adjustment.rejectedAt && <DecisionDetail label="Rechazo" value={`${adjustment.rejectionReason || "Sin motivo"} · Usuario #${adjustment.rejectedByUserId ?? "-"} · ${formatDateTime(adjustment.rejectedAt)}`} />}
              {adjustment.cancelledAt && <DecisionDetail label="Cancelación" value={`${adjustment.cancellationReason || "Sin motivo"} · Usuario #${adjustment.cancelledByUserId ?? "-"} · ${formatDateTime(adjustment.cancelledAt)}`} />}
            </div>
          </DecisionSection>

          {isClosed && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
              Esta modificación está resuelta y queda en modo lectura.
            </div>
          )}

          {canDecide && pendingAction && (
            <div className="space-y-3 rounded-md border p-3">
              {pendingAction === "approve" && (
                <div className="flex items-start gap-2 text-sm">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <span>{approvalCopy}</span>
                </div>
              )}
              {pendingAction === "reject" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Motivo de rechazo</label>
                  <Textarea value={reason} onChange={event => setReason(event.target.value)} placeholder="Indica el motivo de rechazo" />
                  {rejectReasonRequired && <p className="text-xs text-red-600">El motivo de rechazo es obligatorio.</p>}
                  <p className="text-sm text-muted-foreground">Esta modificación no cambiará el importe contractual.</p>
                </div>
              )}
              {pendingAction === "cancel" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Motivo de cancelación</label>
                  <Textarea value={reason} onChange={event => setReason(event.target.value)} placeholder="Indica el motivo de cancelación" />
                  {cancelReasonRequired && <p className="text-xs text-red-600">El motivo de cancelación es obligatorio.</p>}
                  <p className="text-sm text-muted-foreground">La modificación quedará cancelada y se conservará en el historial.</p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={() => pendingAction ? setPendingAction(null) : onOpenChange(false)} disabled={decisionPending}>
            Volver
          </Button>
          {canDecide && isPending && (
            <div className="flex flex-wrap justify-end gap-2">
              {!pendingAction && (
                <>
                  <Button variant="outline" onClick={() => setPendingAction("cancel")} disabled={decisionPending}>
                    Cancelar solicitud
                  </Button>
                  <Button variant="outline" onClick={() => setPendingAction("reject")} disabled={decisionPending}>
                    <XCircle className="mr-2 h-4 w-4" />Rechazar
                  </Button>
                  <Button onClick={() => setPendingAction("approve")} disabled={decisionPending || approveBlockedByEvidence}>
                    <CheckCircle2 className="mr-2 h-4 w-4" />Aprobar
                  </Button>
                </>
              )}
              {pendingAction === "approve" && (
                <Button onClick={() => runDecision("approve")} disabled={decisionPending || approveBlockedByEvidence}>
                  {decisionPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Aprobar modificación
                </Button>
              )}
              {pendingAction === "reject" && (
                <Button variant="destructive" onClick={() => runDecision("reject")} disabled={decisionPending || rejectReasonRequired}>
                  {decisionPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Rechazar modificación
                </Button>
              )}
              {pendingAction === "cancel" && canCancelContractAdjustment(user?.role, adjustment.status) && (
                <Button variant="destructive" onClick={() => runDecision("cancel")} disabled={decisionPending || cancelReasonRequired}>
                  {decisionPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Cancelar modificación
                </Button>
              )}
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
